import styles from "./styles.module.scss"
import { useEffect, useMemo, useRef, KeyboardEvent } from "react"
import { makeAutoObservable } from "mobx"
import { observer } from "mobx-react-lite"
import { DefaultInput } from "../DefaultInput"
import { colors } from "@theme/colors"
import { ICruiseLine } from "@cruisen/contract"
import { ErrorHandledImage } from "../ErrorHandledImage"
import { DefaultSpinner } from "../../loaders/DefaultSpinner"

class CruiseLineListState {
  search: string
  highlightedIndex: number

  constructor() {
    this.search = ""
    this.highlightedIndex = -1

    makeAutoObservable(this)
  }

  setSearch(value: string) {
    this.search = value
  }

  setHighlightedIndex(value: number) {
    this.highlightedIndex = value
  }
}

export const CruiseLinesList = observer(
  ({
    onSelect,
    onClose,
    containerWidth,
    cruiseLines,
    loading,
  }: {
    onSelect: (iso2: string, country?: string) => void
    onClose?: () => void
    containerWidth?: number
    withPhonePrefix?: boolean
    cruiseLines: ICruiseLine[]
    loading: boolean
  }) => {
    const state = useMemo(() => new CruiseLineListState(), [])
    const inputRef = useRef<HTMLInputElement>(null)
    const listRef = useRef<HTMLDivElement>(null)

    const filteredCruiseLines = cruiseLines?.filter((cruiseLine) =>
      cruiseLine.name.toLowerCase().includes(state.search.toLowerCase()),
    )

    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.focus()
      }
    }, [])

    useEffect(() => {
      if (filteredCruiseLines.length > 0 && state.search.length > 0) {
        state.setHighlightedIndex(0)
      } else {
        state.setHighlightedIndex(-1)
      }
    }, [state.search, filteredCruiseLines.length])

    useEffect(() => {
      if (state.highlightedIndex >= 0 && listRef.current) {
        const highlightedElement = listRef.current.querySelector(
          `#cruise-line-${state.highlightedIndex}`,
        ) as HTMLElement
        if (highlightedElement) {
          highlightedElement.scrollIntoView({ block: "nearest" })
        }
      }
    }, [state.highlightedIndex])

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (filteredCruiseLines.length === 0) return

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault()
          state.setHighlightedIndex(
            state.highlightedIndex < filteredCruiseLines.length - 1
              ? state.highlightedIndex + 1
              : state.highlightedIndex,
          )
          break
        case "ArrowUp":
          e.preventDefault()
          state.setHighlightedIndex(
            state.highlightedIndex > 0 ? state.highlightedIndex - 1 : state.highlightedIndex,
          )
          break
        case "Enter":
          e.preventDefault()
          if (state.highlightedIndex >= 0) {
            const cruiseLine = filteredCruiseLines[state.highlightedIndex]
            onSelect(cruiseLine.id.toString(), cruiseLine.name)
          }
          break
        case "Escape":
          if (onClose) onClose()
          break
      }
    }

    return (
      <div className={styles.cruiseLineListWrapper} style={{ width: containerWidth || "auto" }}>
        <div className={styles.searchWrapper}>
          <DefaultInput
            name="cruise-line-search"
            value={state.search}
            onChange={(value) => state.setSearch(value || "")}
            onKeyDown={handleKeyDown}
            innerRef={inputRef}
            label="Search for a cruise line"
            parentBackgroundColor={colors.backgroundQuaternary}
          />
        </div>
        {loading ? (
          <div className={styles.loading}>
            <DefaultSpinner />
          </div>
        ) : (
          <div className={styles.cruiseLineList} ref={listRef}>
            {filteredCruiseLines.map((cruiseLine, index) => (
              <button
                type="button"
                key={cruiseLine.id}
                id={`cruise-line-${index}`}
                className={`${styles.cruiseLine} ${index === state.highlightedIndex ? styles.highlighted : ""}`}
                onClick={() => onSelect(cruiseLine.id.toString(), cruiseLine.name)}
              >
                <div className={styles.logoWrapper}>
                  <ErrorHandledImage
                    uri={cruiseLine.logoUrl}
                    alt={cruiseLine.name}
                    className={styles.logo}
                  />
                </div>
                <p>{cruiseLine.name}</p>
              </button>
            ))}
          </div>
        )}
      </div>
    )
  },
)
